import React, { FC, useCallback } from 'react';
import { useHistory } from 'react-router';

import { MasterOutlet, MasterOutletUpdate } from '@app/api/models/acb';
import { QueryTable } from '@app/components/common/table';
import { useApi, useFetchData } from '@app/hooks';
import { ROLES } from '@app/navigation/types';
import { usePermissions } from '@app/hooks/use-permissions';
import { RequestPayload } from '@app/api/types';
import { getRoleName } from '@app/utils';
import Error404 from '@app/pages/errors/404';

import { canAccessApprovalQueue, requestQueueFilter } from './utils/approval';
import { onlyProcessFields } from './utils/process-type';
import { useColumns } from './columns';

const enrichUpdateWithOutlet = (
  update: MasterOutletUpdate,
  outlet?: MasterOutlet
): MasterOutletUpdate => {
  if (!outlet) {
    return update;
  }

  return {
    ...update,
    master_outlet: {
      name: outlet.name,
      title: outlet.title,
      address: outlet.address,
      visits_day: outlet.visits_day,
    },
  };
};

const UpdatesOutlets: FC<{ approvalQueue?: boolean }> = ({ approvalQueue = false }) => {
  const api = useApi();
  const canEdit = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF, ROLES.TRADE_MARKETING] });
  const columns = useColumns({ canEdit, approvalQueue });
  const history = useHistory();

  const getUpdates = useCallback(
    async (payload: RequestPayload) => {
      const role = getRoleName();
      
      if (role === ROLES.TRADE_MARKETING) {
        payload.filter.process_type = ['channels'];
      }

      const response = await api.acb.getUpdates({ ...payload, filter: requestQueueFilter(role, payload.filter, approvalQueue) });

      const updates = (response?.data?.data || []).map(onlyProcessFields);
      if (!updates.length) {
        return { ...response, data: { ...response.data, data: updates } };
      }

      const outletIds = [...new Set(updates.map((item) => item.outlet_id).filter(Boolean))];
      const outletsById = new Map<number, MasterOutlet>();

      await Promise.all(
        outletIds.map(async (outletId) => {
          try {
            const outletResponse = await api.acb.getMasterOutlet(outletId);
            if (outletResponse?.data) {
              outletsById.set(outletId, outletResponse.data);
            }
          } catch {
            // Не блокируем список, если одну ТТ не удалось загрузить
          }
        })
      );

      return {
        ...response,
        data: {
          ...response.data,
          data: updates.map((update) =>
            enrichUpdateWithOutlet(update, outletsById.get(update.outlet_id))
          ),
        },
      };
    },
    [api, approvalQueue]
  );

  const [fetchData] = useFetchData<MasterOutletUpdate>({ method: getUpdates });
  if (approvalQueue && !canAccessApprovalQueue(getRoleName())) { return <Error404 />; }
  return (
    <>
      <QueryTable<MasterOutletUpdate>
        key={approvalQueue ? 'approval' : 'updates'}
        title={approvalQueue ? 'Согласование ТТ' : 'Изменение ТТ'}
        columns={columns}
        onCreateClick={approvalQueue ? undefined : () => history.push('/acl/updates-outlets/create')}
        fetchData={fetchData}
      />
    </>
  );
};

export default UpdatesOutlets;
